import type { AccountHistoricalComparisonItem } from '@/models/AccountViewModel';
import {
  aggregateHistoricalComparisons,
  type HistoricalComparisonAmounts,
} from '@/utils/historicalBalance';

export interface SectionTotals extends HistoricalComparisonAmounts {
  isSplit: false;
  count: number;
}

export interface SplitGroupTotals {
  isSplit: true;
  count: number;
}

export type GroupTotals = SectionTotals | SplitGroupTotals;

export type GroupTotalsByPath = Map<string, GroupTotals>;

export interface SectionLabels {
  assets: string;
  liabilities: string;
}

export interface GroupedHistoricalRecord {
  record: AccountHistoricalComparisonItem;
  groupName: string;
  path: string[];
  sortIndex: number;
}

export const groupPathKey = (path: readonly string[]): string =>
  JSON.stringify(path);

export const isSplitGroup = (
  totals: GroupTotals | undefined,
): totals is SplitGroupTotals => totals?.isSplit === true;

const accountPathKey = (record: AccountHistoricalComparisonItem): string =>
  `${record.Name}_${String(record.Id)}`;

export function groupHistoricalRecords(
  records: readonly AccountHistoricalComparisonItem[],
  getGroupName: (record: AccountHistoricalComparisonItem) => string,
  labels: SectionLabels,
): { entries: GroupedHistoricalRecord[]; totalsByPath: GroupTotalsByPath } {
  const recordsByGroup = new Map<string, AccountHistoricalComparisonItem[]>();
  for (const record of records) {
    const groupName = getGroupName(record);
    const bucket = recordsByGroup.get(groupName);
    if (bucket) {
      bucket.push(record);
    } else {
      recordsByGroup.set(groupName, [record]);
    }
  }

  const entries: GroupedHistoricalRecord[] = [];
  const totalsByPath: GroupTotalsByPath = new Map();
  const addSection = (
    path: string[],
    groupName: string,
    sectionRecords: AccountHistoricalComparisonItem[],
  ) => {
    sectionRecords.forEach((record, index) => {
      entries.push({
        record,
        groupName,
        path: [...path, accountPathKey(record)],
        sortIndex: index,
      });
    });
    totalsByPath.set(groupPathKey(path), {
      ...aggregateHistoricalComparisons(sectionRecords),
      isSplit: false,
      count: sectionRecords.length,
    });
  };

  const sortedGroupNames = [...recordsByGroup.keys()].sort((a, b) =>
    a.localeCompare(b),
  );
  for (const groupName of sortedGroupNames) {
    const groupRecords = recordsByGroup.get(groupName) ?? [];
    const assets = groupRecords.filter((record) => record.IsAsset);
    const liabilities = groupRecords.filter((record) => !record.IsAsset);

    if (assets.length === 0 || liabilities.length === 0) {
      addSection([groupName], groupName, groupRecords);
      continue;
    }

    totalsByPath.set(groupPathKey([groupName]), {
      isSplit: true,
      count: groupRecords.length,
    });
    addSection([groupName, labels.assets], groupName, assets);
    addSection([groupName, labels.liabilities], groupName, liabilities);
  }

  return { entries, totalsByPath };
}
